import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import moment from 'moment';

import { reportService, itemsPerPage } from '../../Services/index.js';

import {ReactComponent as Loading} from '../../images/icons/General/loading-01.svg';
import {ReactComponent as Download} from '../../images/icons/General/download-cloud-02.svg';
import {ReactComponent as Printer} from '../../images/icons/Media/printer.svg';

import styles from '../../styles/components/users/letter.js';

export const AAUSDetails = () => {
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const year = params.year;
    const type = params.type;
    const [list, setList] = useState([]);
    const [sortField, setSortField] = useState('date');
    const [sortOrder, setSortOrder] = useState(false);
    const [page, setPage] = useState(-1);
    const [hasMore, setHasMore] = useState(true);
    const loaderRef = useRef(null);
    const tableRef = useRef(null);
    const initialPageRef = useRef(Math.max(parseInt(searchParams.get('page')) || 1, 1) - 1);
    const firstLoadRef = useRef(true);
    const scrollToIndexRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [loadingCsv, setLoadingCsv] = useState(false);
    const types = {
        sci: 'Dives by Classification: Scientific',
        protr: 'Dives by Classification: Training and Proficiency',
        ocs: 'Dives by Diving Mode: Open Circuit SCUBA',
        hoo: 'Dives by Diving Mode: Hookah',
        ss: 'Dives by Diving Mode: Surface Supplied',
        reb: 'Dives by Breathing Gas: Rebreather',
        air: 'Dives by Breathing Gas: Air',
        nitrox: 'Dives by Breathing Gas: Nitrox',
        mixed: 'Dives by Breathing Gas: Mixed Gas',
        tables: 'Dives by Decompression Profiling Method: Dive Tables',
        computer: 'Dives by Decompression Profiling Method: Dive Computer',
        soft: 'Dives by Decompression Profiling Method: PC-based Deco Software',
        rd: 'Dives by Specialized Diving Environment: Required Decompression',
        oe: 'Dives by Specialized Diving Environment: Overhead Environment',
        bw: 'Dives by Specialized Diving Environment: Blue Water',
        ip: 'Dives by Specialized Diving Environment: Ice/Polar',
        sd: 'Dives by Specialized Diving Environment: Saturation Diving',
        ad: 'Dives by Specialized Diving Environment: Aquarium Diving',
        30: 'Scientific or Training/Proficiency Dives by AAUS Depth Range: 0 - 30 feet',
        60: 'Scientific or Training/Proficiency Dives by AAUS Depth Range: 31 - 60 feet',
        100: 'Scientific or Training/Proficiency Dives by AAUS Depth Range: 61 - 100 feet',
        130: 'Scientific or Training/Proficiency Dives by AAUS Depth Range: 101 - 130 feet',
        150: 'Scientific or Training/Proficiency Dives by AAUS Depth Range: 131 - 150 feet',
        190: 'Scientific or Training/Proficiency Dives by AAUS Depth Range: 151 - 190 feet',
        191: 'Scientific or Training/Proficiency Dives by AAUS Depth Range: 190 - > feet',
    }

    const date = new Date();
    const orgName = useSelector((state) => state.org.name);

    const resetValues = () => {
        setList([]);
        setPage(-1);
        setHasMore(true);
    }

    const sort = (e) => {
        if (e.target.id === sortField)
            setSortOrder(!sortOrder)
        else 
            setSortField(e.target.id);

        resetValues();
    }

    const downloadCsv = async () => {
        if (year) {
            setLoadingCsv(true);
            const date = new Date();
            const res = await reportService.AAUSReportExport(year);
            const a = document.createElement('a');
            let binaryData = [];
            binaryData.push(res);
            let url = window.URL.createObjectURL(new Blob(binaryData, {type: "text/csv"}))
            a.href = url;
            a.download = 'AAUS-Report_'+window.location.hostname+'_'+moment(date).format('YYYY-MM-DD-HH-mm')+'.csv';
            document.body.append(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            setLoadingCsv(false);
        }
    };

    const fetchData = useCallback(async (pageNum, pagesCount, sortFieldValue, sortOrderValue) => {
        const res = await reportService.aausDetails(year, type, pageNum, sortFieldValue, sortOrderValue, pagesCount);
        setList(prev => [...prev, ...res.list]);

        if (pagesCount > 1)
            scrollToIndexRef.current = itemsPerPage * (pagesCount - 1);

        if (res.list.length < itemsPerPage * pagesCount)
            setHasMore(false);
        }, [year, type]);

    useEffect(() => {
        const observer = new IntersectionObserver(
        (entries) => {
            if (entries[0].isIntersecting && hasMore) {
                setPage((prev) => prev === -1 && firstLoadRef.current ? initialPageRef.current : prev + 1);
            }
        },
        { threshold: 0.1 }
        );

        const loader = loaderRef.current;
        if (loader) observer.observe(loader);

        return () => {
            if (loader) observer.unobserve(loader);
        };
    }, [hasMore]);

    useEffect(() => {
        if (page < 0) return;
        // on the first load fetch all pages up to the one from the url in one request
        const pagesCount = firstLoadRef.current ? page + 1 : 1;
        firstLoadRef.current = false;
        fetchData(page + 1 - pagesCount, pagesCount, sortField, sortOrder);
    }, [fetchData, page, sortField, sortOrder]);

    useEffect(() => {
        if (page < 0) return;
        const urlPage = page > 0 ? String(page + 1) : null;
        if (searchParams.get('page') === urlPage) return;
        const newParams = new URLSearchParams(searchParams);
        if (urlPage)
            newParams.set('page', urlPage);
        else
            newParams.delete('page');
        setSearchParams(newParams, { replace: true });
    }, [page, searchParams, setSearchParams]);

    useEffect(() => {
        const index = scrollToIndexRef.current;
        if (index === null) return;
        scrollToIndexRef.current = null;
        const row = tableRef.current?.tBodies[0]?.rows[index];
        if (row)
            row.scrollIntoView({ block: 'start' });
    }, [list]);

    if (!types[type]) return (<div className="error-404">There's nothing here: 404!</div>)
    
    return (
        <>
            <h1 id="report-title">{orgName} {year} AAUS Statistics Filed {moment(date).format('YYYY-MM-DD-HH-mm')}</h1>
            <div className="bordered-content">
                <div className="header">
                    <h4>{types[type] || ''}</h4>
                </div>
                <table id="history-table" ref={tableRef}>
                    <thead>
                        <tr>
                            <th className={`sortable ${sortField === 'diver' ? (sortOrder ? 'down' : 'up') : ''}`} id="diver" onClick={sort}>Diver</th>
                            <th className={`sortable ${sortField === 'date' ? (sortOrder ? 'down' : 'up') : ''}`} id="date" onClick={sort}>Date</th>
                            <th className={`sortable ${sortField === 'time' ? (sortOrder ? 'down' : 'up') : ''}`} id="time" onClick={sort}>Starting time</th>
                            <th className={`sortable ${sortField === 'totalTime' ? (sortOrder ? 'down' : 'up') : ''}`} id="totalTime" onClick={sort}>Duration (min)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.length ? list.map(item => {
                            return (
                                <tr key={item.id}>
                                    <td><Link to={`/configuration/users/${item.userId}`}>{item.lastname}, {item.firstname}</Link></td>
                                    <td><Link to={`/configuration/users/${item.userId}/dives/${item.id}`}>{item.date}<span className="mobile"> {item.time}</span></Link></td>
                                    <td className="desktop">{item.time}</td>
                                    <td><strong className="mobile">Duration: </strong>{item.totalTime}</td>
                                </tr>
                            )}
                        ) : (hasMore ? <></> : <tr><td colSpan="9" className="load-more">No dives found</td></tr>)}
                        {hasMore && <tr ref={loaderRef}><td colSpan="9" className="load-more">Loading more...</td></tr>}
                    </tbody>
                </table>
            </div>
        </>
    );
};

export default AAUSDetails;